import { LocaleObject } from './locale';

type ConfigurableTerms = LocaleObject['ConfigurableTerms'];
export type ConfigurableTermValues = { [K in keyof ConfigurableTerms]?: string };

type TokenIndexEntry = { termKey: keyof ConfigurableTerms; form: 'singular' | 'plural' };

const buildTokenIndex = (terms: ConfigurableTerms): Map<string, TokenIndexEntry> => {
  const index = new Map<string, TokenIndexEntry>();
  (Object.keys(terms) as (keyof ConfigurableTerms)[]).forEach(termKey => {
    const [singularToken, pluralToken] = terms[termKey].tokenNames;
    index.set(singularToken, { termKey, form: 'singular' });
    index.set(pluralToken, { termKey, form: 'plural' });
  });
  return index;
};

const TOKEN_PATTERN = /\{\{(\w+)\}\}/g;

const resolveString = (
  str: string,
  tokenIndex: Map<string, TokenIndexEntry>,
  terms: ConfigurableTerms,
  termValues: ConfigurableTermValues,
): string =>
  str.replace(TOKEN_PATTERN, (match, tokenName) => {
    const entry = tokenIndex.get(tokenName);
    // not a configurable-term token (e.g. {{count}}, {{setName}}) - leave for its own .replace()
    if (!entry) return match;

    const value = termValues[entry.termKey];
    const options = terms[entry.termKey].options as Record<string, { singular: string; plural: string }>;
    const option = value !== undefined ? options[value] : undefined;
    return option ? option[entry.form] : match;
  });

// one-time-per-node taint scan: does this node contain (directly or nested) a {{token}}? Nodes that
// don't are handed back as their original reference forever - no proxy, no re-render churn, ever.
const taintCache = new WeakMap<object, boolean>();

const isTainted = (node: unknown): node is object => {
  if (node === null || typeof node !== 'object') return false;

  const cached = taintCache.get(node);
  if (cached !== undefined) return cached;

  taintCache.set(node, false); // guard against pathological circular structures while scanning
  const tainted = Object.values(node as Record<string, unknown>).some(value => {
    if (typeof value === 'string') return value.includes('{{');
    // function bodies (e.g. `(n) => \`...{{Sets}}...\``) aren't reflectable as data, so sniff their source
    if (typeof value === 'function') return value.toString().includes('{{');
    if (typeof value === 'object' && value !== null) return isTainted(value);
    return false;
  });
  taintCache.set(node, tainted);
  return tainted;
};

const wrapNode = <T>(
  node: T,
  tokenIndex: Map<string, TokenIndexEntry>,
  terms: ConfigurableTerms,
  termValues: ConfigurableTermValues,
  proxyCache: WeakMap<object, unknown>,
): T => {
  if (typeof node !== 'object' || node === null) return node;
  if (!isTainted(node)) return node;

  const cached = proxyCache.get(node);
  if (cached) return cached as T;

  const proxy = new Proxy(node as object, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value === 'string') {
        return value.includes('{{') ? resolveString(value, tokenIndex, terms, termValues) : value;
      }
      if (typeof value === 'function') {
        return (...args: unknown[]) => {
          const result = value.apply(target, args);
          return typeof result === 'string' && result.includes('{{')
            ? resolveString(result, tokenIndex, terms, termValues)
            : result;
        };
      }
      return wrapNode(value, tokenIndex, terms, termValues, proxyCache);
    },
  });

  proxyCache.set(node, proxy);
  return proxy as T;
};

const proxyCachesByTermValues = new WeakMap<ConfigurableTermValues, WeakMap<object, unknown>>();

/**
 * Wraps `language` so any string containing a ConfigurableTerms {{token}} (e.g. {{Set}}/{{Sets}})
 * resolves to the tenant's configured wording. `termValues` must be a stable reference (e.g. from
 * useMemo) - subtrees with no tokens are handed back untouched, and resolved subtrees are cached
 * per termValues identity, so unrelated components never see a reference change.
 */
export const withConfigurableTerms = <T extends LocaleObject>(language: T, termValues: ConfigurableTermValues): T => {
  let proxyCache = proxyCachesByTermValues.get(termValues);
  if (!proxyCache) {
    proxyCache = new WeakMap();
    proxyCachesByTermValues.set(termValues, proxyCache);
  }

  const tokenIndex = buildTokenIndex(language.ConfigurableTerms);
  return wrapNode(language, tokenIndex, language.ConfigurableTerms, termValues, proxyCache);
};
